/*
 * This file is part of the Det Norske Teatret Nettside 2019 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, {
  forwardRef,
  useRef,
  useState,
  useEffect,
  useImperativeHandle,
  type ComponentProps,
  type CSSProperties,
} from 'react';
import NodeTransition from 'components/NodeTransition/NodeTransition';
import minBy from 'utils/minBy';
import useKeys from 'effects/keys';
import useInview from 'effects/inview';
import scrollTo from 'utils/scrollTo';
import Poster, { type PosterHandle, type PosterProps } from './Poster/Poster';
import './Video.scss';

const DEFAULT_SRC_SET: Source[] = [];
const DEFAULT_TEXT_TRACKS: TrackItem[] = [];

const DEFAULT_SCROLL_TO = {
  alignTop: true,
  offset: 64,
};

const DEFAULT_ON_ENDED = () => {};

interface Source {
  link: string;
  width: number;
  orientation?: string;
}

type TrackItem = ComponentProps<'track'> & { srcLang: string };

export interface VideoHandle {
  play: () => void;
  pause: () => void;
}

export interface VideoProps extends Omit<PosterProps, 'rootRef' | 'onPlay'> {
  src?: string | null;
  srcSet?: Source[];
  textTracks?: TrackItem[] | null;
  width?: number;
  height?: number;
  autoPlay?: boolean;
  scrollTo?: { alignTop?: boolean; offset?: number };
  onEnded?: () => void;
}

/**
 * This is the Video component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 */
const Video = forwardRef<VideoHandle, VideoProps>(
  (
    {
      src: propSrc = null,
      srcSet = DEFAULT_SRC_SET,
      textTracks = DEFAULT_TEXT_TRACKS,
      width = 1600,
      height = 900,
      autoPlay = false,
      scrollTo: scrollToOptions = DEFAULT_SCROLL_TO,
      onEnded = DEFAULT_ON_ENDED,
      ...props
    },
    parentRef
  ) => {
    const player = useRef<HTMLVideoElement>(null);
    const posterRef = useRef<PosterHandle>(null);
    const [index, setIndex] = useState<number | null>(null);
    const [poster, setPoster] = useState(true);
    const [focus, setFocus] = useState(false);
    const [ref, inview] = useInview<HTMLDivElement>({ threshold: 0.5 });

    useKeys('video', !poster, {
      esc: () => {
        if (player.current) {
          player.current.pause();
          player.current.currentTime = 0;
          setPoster(true);
          setFocus(true);
        }
      },
    });

    useEffect(() => {
      function onResize() {
        if (srcSet.length) {
          const orientation =
            window.innerWidth < window.innerHeight ? 'portrait' : 'landscape';

          let index = srcSet.findIndex(
            source =>
              source.width <= window.innerWidth &&
              (source.orientation === orientation ||
                source.orientation !== 'portrait')
          );

          if (index < 0) {
            const min = minBy(
              srcSet.filter(({ width }) => !!width),
              ({ width }) => width
            );

            if (min) {
              index = srcSet.findIndex(({ width }) => width === min.width);
            }
          }

          setIndex(index);
        }
      }

      window.addEventListener('resize', onResize);
      onResize();

      return function cleanup() {
        window.removeEventListener('resize', onResize);
      };
    }, [srcSet]);

    useEffect(() => {
      if (!inview) {
        if (player.current) {
          player.current.pause();
          player.current.currentTime = 0;
          setPoster(true);
        }
      }
    }, [inview]);

    useEffect(() => {
      if (focus) {
        posterRef.current!.focus();
        setFocus(false);
      }
    }, [focus]);

    useImperativeHandle(parentRef, () => ({
      play: () => {
        if (player.current) {
          player.current.play();
        }
      },
      pause: () => {
        player.current!.pause();
      },
    }));

    function getSource() {
      if (index === null || index < 0) {
        if (!propSrc) return undefined;
        return propSrc;
      }

      return srcSet[index].link;
    }

    function getTracks() {
      if (!textTracks) return null;

      return textTracks.map(({ srcLang, ...props }) => (
        <track key={srcLang} srcLang={srcLang} {...props} />
      ));
    }

    return (
      <div
        ref={ref}
        className="video"
        style={{ '--ratio': height / width } as CSSProperties}
      >
        <div>
          <video
            ref={player}
            src={getSource()}
            width={width}
            height={height}
            controls={!poster}
            autoPlay={autoPlay}
            onPlay={() => {
              setPoster(false);
              scrollTo(ref.current!, scrollToOptions);
            }}
            onEnded={() => {
              player.current!.currentTime = 0;
              setPoster(true);
              setFocus(true);
              onEnded();
            }}
          >
            {getTracks()}
          </video>
          <NodeTransition in={poster} timeout={1000} unmountOnExit={true}>
            {nodeRef => (
              <Poster
                ref={posterRef}
                rootRef={nodeRef}
                {...props}
                onPlay={() => {
                  player.current!.play();
                }}
              />
            )}
          </NodeTransition>
        </div>
      </div>
    );
  }
);

export default Video;
